import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { Router } from '@angular/router';
import {
  PriceStrategy,
  PriceStrategyStatus,
} from '@api-ui-app/src/app/price-navigator/strategies/types/price-strategy';
import { StrategyAction } from '@api-ui-app/src/app/price-navigator/strategies/types/strategy-action';
import { StrategyActionType } from '@api-ui-app/src/app/price-navigator/strategies/types/strategy-action-type';
import { PRICE_NAVIGATOR_DATE_FORMAT } from '@api-ui-app/src/app/shared/pricing-table/constants/price-navigator.constants';
import { PriceNavigatorEmptyListComponent } from '@api-ui-app/src/app/shared/pricing-table/empty-empty-list/price-navigator-empty-list.component';
import { DropdownMenuModule } from '@gelato-api-ui/sdk/src/lib/shared/dropdown-menu/dropdown-menu.module';
import { LokaliseModule } from '@gelato-api-ui/sdk/src/lib/shared/lokalise/lokalise.module';
import { LogEventDirective } from '@gelato-api-ui/ui-kit/src/lib/analytics/log-event.directive';
import { TranslateModule } from '@ngx-translate/core';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { EmptyListReason } from '@api-ui-app/src/app/shared/pricing-table/constants/price-list-empty-table.constant';

@Component({
  standalone: true,
  selector: 'gd-price-navigator-strategies-overview',
  templateUrl: './price-navigator-strategies-overview.component.html',
  styleUrls: ['./price-navigator-strategies-overview.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    DropdownMenuModule,
    TranslateModule,
    LogEventDirective,
    PriceNavigatorEmptyListComponent,
    NgxSkeletonLoaderModule,
    LokaliseModule,
    PriceNavigatorEmptyListComponent,
  ],
})
export class PriceNavigatorStrategiesOverviewComponent {
  @Input() isLoading: boolean;
  @Input() strategies: PriceStrategy[];
  @Input() set availableStrategyActionTypes(actions: StrategyActionType[]) {
    this.setAvailableStrategyActions(actions);
  }

  @Output() activeStrategy = new EventEmitter<string>();
  @Output() editStrategy = new EventEmitter<string>();
  @Output() deleteStrategy = new EventEmitter<PriceStrategy>();
  @Output() duplicateStrategy = new EventEmitter<string>();
  @Output() publishPricesNow = new EventEmitter<string>();

  availableStrategyActions: StrategyAction[];
  emptyListReasons = [EmptyListReason.STRATEGIES_OVERVIEW_NO_PRODUCTS];

  readonly dateFormat = PRICE_NAVIGATOR_DATE_FORMAT;
  readonly datePlaceholder = '---  --, ----';
  readonly loaderRowsArray = Array.from(Array(3));
  private readonly strategyActions: StrategyAction[] = [
    {
      key: StrategyActionType.edit,
      displayName: 'txt_edit',
      analyticsKey: 'Price Strategy Edited',
      handler: ({ id }: PriceStrategy) => this.onEditStrategyClick(id),
    },
    {
      key: StrategyActionType.duplicate,
      displayName: 'txt_duplicate',
      analyticsKey: 'Price Strategy Duplicated',
      handler: ({ id }: PriceStrategy) => this.onDuplicateStrategyClick(id),
    },
    {
      key: StrategyActionType.activate,
      displayName: 'txt_activate',
      analyticsKey: 'Price Strategy Activated',
      handler: ({ id }: PriceStrategy) => this.onActiveStrategyClick(id),
      condition: this.isActiveButtonShown,
    },
    {
      key: StrategyActionType.publish,
      displayName: 'txt_publish_now',
      analyticsKey: 'Price Strategy Published Now',
      handler: strategy => this.onPublishNowClick(strategy),
      condition: this.isPublishNowButtonShown,
      getCssClasses: strategy => ({ disabled: this.isPublishNowButtonDisabled(strategy) }),
    },
    {
      key: StrategyActionType.delete,
      displayName: 'txt_delete',
      analyticsKey: 'Price Strategy Deleted',
      handler: strategy => this.onDeleteStrategyClick(strategy),
      getCssClasses: () => ({ 'item-action-delete': true }),
    },
  ];

  constructor(private router: Router) {}

  onActiveStrategyClick(strategyId: string) {
    this.activeStrategy.emit(strategyId);
  }

  onPublishNowClick(strategy: PriceStrategy) {
    if (this.isPublishNowButtonDisabled(strategy)) {
      return;
    }
    this.publishPricesNow.emit(strategy.id);
  }

  navigateToStrategy(strategyId: string) {
    this.router.navigate(['/price-navigator/strategies/edit', strategyId]);
  }

  onEditStrategyClick(strategyId: string) {
    this.navigateToStrategy(strategyId);
  }

  onDeleteStrategyClick(strategy: PriceStrategy) {
    this.deleteStrategy.emit(strategy);
  }

  onDuplicateStrategyClick(strategyId: string) {
    this.duplicateStrategy.emit(strategyId);
  }

  isActiveButtonShown(strategy: PriceStrategy) {
    return strategy.status !== PriceStrategyStatus.ACTIVE;
  }

  isPublishNowButtonShown(strategy: PriceStrategy) {
    return strategy.status === PriceStrategyStatus.ACTIVE;
  }

  isPublishNowButtonDisabled(strategy: PriceStrategy): boolean {
    return !strategy.numberOfAssignedProducts;
  }

  private setAvailableStrategyActions(actions?: StrategyActionType[]) {
    this.availableStrategyActions = actions ? this.strategyActions.filter(({ key }) => actions.includes(key)) : [];
  }
}
